import cc from 'classcat';
import { Button, Dropdown, IMenuItem, InputFormik, TextAreaFormik, Img } from '../../Common';
import { useTranslation } from '../../../hooks/useTranslation';
import { Formik, FormikProps } from 'formik';
import { ISubmitHelpers } from '../../../commonTypes/formik.types';
import { UserDocumentDto, UserDocumentPostDto } from '../../../commonTypes/user.types';
import { useEffect, useState } from 'react';
import { useContainer, useHttpPutUploadUserDocument } from '../../../hooks';
import useHttpPatchDocument from '../../../hooks/user/userDocuments/useHttpPatchDocument';
import { ToastStateHandler } from '../../../api';
import { ADD_EDIT_DOCUMENT_SCHEMA } from './AddEditDocumentSchema';

interface IUploadProps {
    close: () => void;
    menuItems: IMenuItem[];
    document?: UserDocumentDto;
    userSub: string;
    onUploaded: (userDocument: UserDocumentDto) => void;
    onUpdated: () => void;
    uploadError: { fileError: boolean; categoryError: boolean };
    setUploadError: React.Dispatch<React.SetStateAction<{ fileError: boolean; categoryError: boolean }>>;
}

interface IAddEditDocument {
    categoryId?: string;
    categoryName?: string;
    name: string;
    description: string;
}

const NEW_DOCUMENT_DEFAULT_VALUES: IAddEditDocument = {
    categoryId: '',
    categoryName: '',
    name: '',
    description: '',
};
export const UploadEditDocumentModal = ({
    document,
    close,
    menuItems,
    userSub,
    onUploaded,
    onUpdated,
    uploadError,
    setUploadError,
}: IUploadProps) => {
    const { t } = useTranslation();
    const toastHandler = useContainer(ToastStateHandler);
    const { uploadUserDocument } = useHttpPutUploadUserDocument();
    const { updateDocument } = useHttpPatchDocument();

    const [file, setFile] = useState<File>(new File([], ''));
    const [selectedCategoryForUpload, setSelectedCategoryForUpload] = useState<IMenuItem>(
        document
            ? { value: document.categoryId as string, label: document.categoryName as string }
            : {
                  value: '',
                  label: t('newNotifications.categories.selectCategory'),
              }
    );

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, context: FormikProps<IAddEditDocument>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setFile(file);

        if (!context.values.name) context.setFieldValue('name', file.name);
    };
    const handleChangeCategoryForUpload = (selectedCategoryForUpload: IMenuItem, context: FormikProps<IAddEditDocument>) => {
        setSelectedCategoryForUpload(selectedCategoryForUpload);
        context.setFieldValue('categoryId', selectedCategoryForUpload.value);
        context.setFieldValue('categoryName', selectedCategoryForUpload.label);
    };

    const getInitialValues = () => {
        if (!document) return NEW_DOCUMENT_DEFAULT_VALUES;
        return {
            ...document,
            description: document.description ?? NEW_DOCUMENT_DEFAULT_VALUES.description,
        };
    };

    const handleSubmit = (values: IAddEditDocument, { resetForm }: ISubmitHelpers) => {
        if (uploadError.fileError || uploadError.categoryError) return;
        const uploadFields: UserDocumentPostDto = {
            userSub,
            file,
            name: values.name,
            categoryId: values?.categoryId as string,
            onUploaded: document => onUploaded({ ...document, categoryName: values.categoryName, categoryId: values.categoryId }),
            description: values.description,
        };
        if (!document) {
            uploadUserDocument(uploadFields);
            close();
        } else {
            const updatedFields: Partial<UserDocumentPostDto> = {};
            if (document.name !== values.name) {
                updatedFields.name = values.name;
            }
            if (document.categoryId !== values.categoryId) {
                updatedFields.categoryId = values.categoryId;
            }
            if (document.description !== values.description) {
                updatedFields.description = values.description;
            }
            if (Object.keys(updatedFields).length > 0) {
                updateDocument(userSub, document.id, updatedFields, () => onUpdated());
                close();
            } else {
                toastHandler.push('error', t('newNotifications.userDocuments.editTitle'), t('newNotifications.userDocuments.canNotEditMessage'));
            }
        }
        resetForm();
    };

    useEffect(() => {
        if (file.name) {
            setUploadError(prev => ({ ...prev, fileError: false }));
        }
        if (selectedCategoryForUpload.value) {
            setUploadError(prev => ({ ...prev, categoryError: false }));
        }
    }, [file.name, selectedCategoryForUpload]);

    return (
        <Formik initialValues={getInitialValues()} onSubmit={handleSubmit} validationSchema={ADD_EDIT_DOCUMENT_SCHEMA(t)}>
            {context => {
                return (
                    <form onSubmit={context.handleSubmit}>
                        <div className={cc(['yes-no', 'documents__upload-modal'])}>
                            <h3 className="yes-no__title">
                                {document ? t('newNotifications.userDocuments.editTitle') : t('newNotifications.userDocuments.uploadTitle')}
                            </h3>
                            <div className={'yes-no__description'}>
                                {!document && (
                                    <label htmlFor="document-upload">
                                        <div
                                            className={`${uploadError.fileError && 'input-style__field--error documents__upload-container--rounded'}`}
                                        >
                                            <div
                                                className={`documents__upload-container documents__upload-container--rounded ${
                                                    !uploadError.fileError && 'documents__upload-container--dashed-border'
                                                }`}
                                            >
                                                <Img src={file.name ? '/svg/circle-check-contained.svg' : '/svg/folder-add.svg'} alt="Upload" />
                                                <p className="documents__upload-text--blue">
                                                    {file.name ? t('documents.fileUploaded') : t('documents.documentUpload')}
                                                </p>
                                                <p>{t('documents.maximumSize', { fileSize: '10' })}</p>

                                                <input
                                                    id="document-upload"
                                                    type="file"
                                                    accept=".pdf"
                                                    onChange={event => handleFileChange(event, context)}
                                                    style={{ display: 'none' }}
                                                />
                                            </div>
                                        </div>
                                    </label>
                                )}

                                <Dropdown
                                    isError={uploadError.categoryError}
                                    label={t('documents.category')}
                                    menuItems={menuItems}
                                    selected={selectedCategoryForUpload}
                                    onChange={selectedCategoryForUpload => handleChangeCategoryForUpload(selectedCategoryForUpload, context)}
                                    id="categoryId"
                                />

                                <InputFormik
                                    className="documents__name-input"
                                    id="name"
                                    type="text"
                                    label={t('common.name')}
                                    context={context}
                                    placeholder={t('common.name')}
                                    maxLength={50}
                                    isRequired
                                />
                                <TextAreaFormik
                                    className=""
                                    context={context}
                                    id="description"
                                    label={t('common.description')}
                                    placeholder={t('common.addDescription')}
                                />
                            </div>
                            <div className="yes-no__buttons">
                                <Button onClick={close} text={t('common.cancel')} variant={'text'} color="blue" />
                                <Button
                                    text={document ? t('common.save') : t('common.upload')}
                                    isSubmit
                                    variant={'contained'}
                                    onClick={() => {
                                        if (!document) {
                                            if (!file.name || !context.values.categoryId) {
                                                setUploadError({
                                                    fileError: !file.name,
                                                    categoryError: !context.values.categoryId,
                                                });
                                            } else {
                                                setUploadError({ fileError: false, categoryError: false });
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </form>
                );
            }}
        </Formik>
    );
};
